import * as Sentry from '@sentry/browser';
import * as React from 'react';

import ErrorFallback from './ErrorFallback';

type Props = { children: React.ReactNode; resetKey: string };
type State = { error: Error | null; eventId: string | null };

/** Top-level boundary: catches render errors and reports them to Sentry. */
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, eventId: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const eventId = Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
      tags: { 'error.boundary': 'top-level' },
    });
    this.setState({ eventId });
  }

  componentDidUpdate(prevProps: Props) {
    // eslint-disable-next-line react/prop-types -- Props are typed above
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, eventId: null });
    }
  }

  render() {
    if (this.state.error) {
      return <ErrorFallback eventId={this.state.eventId} />;
    }
    // eslint-disable-next-line react/prop-types -- Props are typed above
    return this.props.children;
  }
}
